import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import AuthViewController from "../redux/actions/AuthViewController";
import { useNavigate } from "react-router-dom";
import AuthLayout from "../components/auth/AuthLayout";
import PwaInstallPrompt from "../components/PwaInstallPrompt";
import { BRAND, inputSx, primaryButtonSx } from "../components/auth/authTheme";

const LoginPage = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const dispatch = useDispatch();
  const { login } = AuthViewController();
  const navigate = useNavigate();

  const onSubmit = (data) => {
    dispatch(login(data));
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your account to continue"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="identifier"
          control={control}
          rules={{
            required: "Email or Mobile Number is required",
            validate: (value) => {
              const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
              const isMobile = /^[0-9]{10}$/.test(value);
              return isEmail || isMobile || "Please enter a valid email address or 10-digit mobile number";
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Email or Mobile Number"
              fullWidth
              error={!!errors.identifier}
              helperText={errors.identifier?.message}
              variant="outlined"
              autoComplete="email"
              sx={{ ...inputSx, mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          rules={{
            required: "Password is required",
            minLength: {
              value: 6,
              message: "Password must be at least 6 characters long",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Password"
              fullWidth
              type={showPassword ? "text" : "password"}
              error={!!errors.password}
              helperText={errors.password?.message}
              variant="outlined"
              autoComplete="current-password"
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      sx={{ color: BRAND.muted }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1.5,
            mb: 3,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                size="small"
                sx={{
                  color: BRAND.border,
                  "&.Mui-checked": { color: BRAND.orange },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: BRAND.text }}>
                Remember me
              </Typography>
            }
          />
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => navigate("/forgot-password")}
            sx={{
              color: BRAND.orange,
              fontWeight: 600,
              cursor: "pointer",
              border: "none",
              background: "none",
              fontFamily: "inherit",
              fontSize: "0.875rem",
            }}
          >
            Forgot password?
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disableElevation
          sx={primaryButtonSx}
        >
          Sign in
        </Button>

        <PwaInstallPrompt />
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
